import { Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';


import { FarmerLayout } from '../../components/farmer-layout/farmer-layout';
import mobileClasses from '../../styles/farmer-mobile.module.css';
import { extractClientPhotoExif } from '../../utils/extract-client-photo-exif';
import { formatDeviceHeading } from '../../utils/format-device-heading';
import { formatGpsCoordinate } from '../../utils/format-gps-coordinate';
import { normalizePhotoForUpload } from '../../utils/normalize-photo-for-upload';
import { parseHarvestProofApiError } from '../../utils/parse-api-error';

import { FarmerHarvestProofPage } from './page';

import {
  harvestProofApi,
  harvestProofQueries,
  HarvestProofParcelStatus,
  isHarvestProofAnswerSubmitted,
} from '@harvest-proof/data-access';

interface FarmerHarvestProofRouteProps {
  token: string;
}

export const FarmerHarvestProofRoute: FC<FarmerHarvestProofRouteProps> = ({
  token,
}) => {
  const queryClient = useQueryClient();
  const formQuery = useQuery(harvestProofQueries.publicForm(token));

  const submitMutation = useMutation({
    mutationFn: async ({
      answerId,
      photo,
      latitude,
      longitude,
      deviceHeading,
      clientCapturedAt,
    }: {
      answerId: number;
      photo: File;
      latitude: number;
      longitude: number;
      deviceHeading: number | null;
      clientCapturedAt: string;
    }) => {
      const clientPhotoExif = await extractClientPhotoExif(photo, {
        captureLatitude: latitude,
        captureLongitude: longitude,
      });
      const normalizedPhoto = await normalizePhotoForUpload(photo);
      const formData = new FormData();
      formData.append('photo', normalizedPhoto);
      formData.append('latitude', formatGpsCoordinate(latitude));
      formData.append('longitude', formatGpsCoordinate(longitude));
      if (deviceHeading != null) {
        formData.append('device_heading', formatDeviceHeading(deviceHeading));
      }
      formData.append('client_captured_at', clientCapturedAt);
      formData.append('client_photo_exif', JSON.stringify(clientPhotoExif));
      return harvestProofApi.submitAnswer({ token, answerId, formData });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: parseHarvestProofApiError(error),
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        harvestProofQueries.publicForm(token).queryKey,
        (previous) => {
          if (!previous) {
            return previous;
          }

          return {
            ...previous,
            status: data.form_status,
            answers: previous.answers.map((answer) =>
              answer.id === data.answer.id
                ? {
                    ...answer,
                    status: isHarvestProofAnswerSubmitted(data.answer)
                      ? HarvestProofParcelStatus.PROOF_RECEIVED
                      : (data.answer.status as HarvestProofParcelStatus),
                    server_submitted_at: data.answer.server_submitted_at ?? null,
                  }
                : answer,
            ),
          };
        },
      );
      notifications.show({
        color: 'green',
        message: 'Preuve envoyée.',
      });
    },
  });

  if (formQuery.isLoading) {
    return (
      <FarmerLayout>
        <Text>Chargement…</Text>
      </FarmerLayout>
    );
  }

  if (formQuery.isError || !formQuery.data) {
    return (
      <FarmerLayout>
        <Stack className={mobileClasses.messageBlock} gap="sm">
          <Text fw={700} size="lg">
            Lien invalide ou expiré
          </Text>
          <Text c="dimmed" size="md">
            Contactez votre technicien si le problème persiste.
          </Text>
        </Stack>
      </FarmerLayout>
    );
  }

  return (
    <FarmerHarvestProofPage
      form={formQuery.data}
      isSubmitting={submitMutation.isPending}
      onRefetch={async () => {
        await formQuery.refetch();
      }}
      onSubmitAnswer={async (args) => {
        await submitMutation.mutateAsync(args);
      }}
    />
  );
};
